import { useOrganisation } from '@/hooks/useOrganisation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, Mail } from 'lucide-react';

interface BillingGateProps {
  children: React.ReactNode;
}

const BillingGate = ({ children }: BillingGateProps) => {
  const { currentOrg, hasOrg, isDemo, loading } = useOrganisation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // No org membership — let the onboarding flow handle it
  if (!hasOrg) {
    return <>{children}</>;
  }

  // Demo orgs always pass
  if (isDemo) {
    return <>{children}</>;
  }

  if (!currentOrg) {
    return <>{children}</>;
  }

  // Check billing status
  const { billing_status, trial_ends_at } = currentOrg;

  // Active billing — pass through
  if (billing_status === 'active') {
    return <>{children}</>;
  }

  // Trial — check if expired
  if (billing_status === 'trial') {
    const trialEnd = trial_ends_at ? new Date(trial_ends_at) : null;
    const isExpired = trialEnd && trialEnd < new Date();

    if (!isExpired) {
      return <>{children}</>;
    }

    // Trial expired
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg text-center">
          <CardHeader>
            <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-warning/10 flex items-center justify-center">
              <Clock className="h-7 w-7 text-warning" />
            </div>
            <CardTitle className="text-2xl">Trial Period Ended</CardTitle>
            <CardDescription className="text-base">
              Your 14-day trial for <strong>{currentOrg.name}</strong> has expired.
              Contact our sales team to activate your subscription.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="w-full">
              <a href="mailto:sales@wellebit.co.uk">
                <Mail className="mr-2 h-4 w-4" />
                Contact Sales
              </a>
            </Button>
            <p className="text-xs text-muted-foreground">
              Your data is safe and will be available once your subscription is activated.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Suspended or cancelled
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg text-center">
        <CardHeader>
          <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <CardTitle className="text-2xl">Subscription Inactive</CardTitle>
          <CardDescription className="text-base">
            The subscription for <strong>{currentOrg.name}</strong> is currently {billing_status}.
            Please contact our team to restore access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <a href="mailto:sales@wellebit.co.uk">
              <Mail className="mr-2 h-4 w-4" />
              Contact Support
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingGate;
